import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    FinancialDocumentSortValue,
    FinancialDocumentView,
    FinancialDocumentViewSchema,
} from "@common/types/views/financialDocumentViews";
import { useScope } from "@state/scopeContext.lib";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { UploadIcon } from "@icons";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { StandardModal } from "@design-system/Modal/Modal";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import interfaceStyles from "../mainInterface/MainInterface.module.scss";
import { queryClient } from "../../queryClient";
import { TablePageHeader } from "../pageHeader/TablePageHeader";
import {
    FINANCIAL_DOCUMENTS_PAGE_SIZE,
    useFinancialDocumentsQuery,
} from "./useFinancialDocumentsQuery";
import { FinancialDocumentDetailsContainer } from "./FinancialDocumentDetails/FinancialDocumentDetailsContainer";
import { FinancialDocumentsDownload } from "./FinancialDocumentsDownload/FinancialDocumentsDownload";
import { FinancialDocumentsUpload } from "./FinancialDocumentUpload/FinancialDocumentsUpload";
import { FinancialDocumentBulkSelectionContextProvider } from "./FinancialDocumentBulkSelection/FinancialDocumentBulkSelectionContextProvider";
import { FinancialDocumentBulkActionsProvider } from "./FinancialDocumentBulkActions/FinancialDocumentBulkActionsProvider";
import { useDocumentDetails } from "./useDocumentDetails";
import { useFinancialDocumentUpload } from "./FinancialDocumentUpload/useFinancialDocumentUpload";
import { DocumentsEmptyState } from "./DocumentsEmptyState";
import { useFinancialDocumentsPageFiltersCache } from "@hooks/view/useFinancialDocumentsPageFiltersCache";
import { useFinancialDocumentFilters } from "./useFinancialDocumentFilters";
import { financialDocumentQueryKeys } from "./lib";
import {
    getDocumentFilters,
    FinancialDocumentFilters,
} from "./FinancialDocumentsPageFilters/lib";
import { FinancialDocumentsViewContainer } from "./FinancialDocumentsView/FinancialDocumentsViewContainer";
import { defaultFinancialDocumentView } from "./FinancialDocumentsView/defaultFinancialDocumentView";
import { financialDocumentsViewConfig } from "./FinancialDocumentsView/financialDocumentsViewConfig";
import { getSortExpression } from "./useFinancialDocumentSort";
import { FinancialDocumentUploadDropzone } from "./FinancialDocumentUpload/FinancialDocumentUploadDropzone";
import { useMaximizedModal } from "./useMaximizedModal";
import { useDocumentDetailsNavigation } from "./useDocumentDetailsNavigation";

interface DocumentsLocationState {
    documentFilters?: FinancialDocumentFilters;
}

function getInitialFiltersFromLocationState(
    state: DocumentsLocationState | null,
): FinancialDocumentFilters | undefined {
    return state?.documentFilters;
}

export const FinancialDocumentsPage: React.FC = () => {
    const { workspaceId } = useScope();
    const [page, setPage] = useState<number>(1);
    const [sort, setSort] = useState<FinancialDocumentSortValue>(
        FinancialDocumentSortValue.UPLOAD_DATE_DESC,
    );
    const { shownDocumentId, showDocumentDetails, closeDetails } =
        useDocumentDetails();
    const { isMaximized, toggleMaximized } = useMaximizedModal(shownDocumentId);
    const onFiltersChange = useCallback(() => setPage(1), []);
    const location = useLocation();
    const [cachedDocumentFilters, setCachedDocumentFilters] =
        useFinancialDocumentsPageFiltersCache();
    const initialFilters =
        getInitialFiltersFromLocationState(location.state) ??
        cachedDocumentFilters;
    const { filters, resetFilters, hasActiveFilters, handleFiltersChange } =
        useFinancialDocumentFilters(onFiltersChange, initialFilters);
    useEffect(() => {
        setCachedDocumentFilters(filters);
    }, [filters, setCachedDocumentFilters]);
    const availableFilters = getDocumentFilters();

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<FinancialDocumentView>(
            WORKSPACE_USER_CACHE_KEYS.FINANCIAL_DOCUMENTS_PAGE_VIEW_CACHE_KEY,
            FinancialDocumentViewSchema,
        );
    const [documentView, setDocumentView] = useState<FinancialDocumentView>(
        cachedView ?? defaultFinancialDocumentView,
    );

    const handleViewChange = useCallback(
        (view: FinancialDocumentView) => {
            setDocumentView(view);
            setCachedView(view);
        },
        [setCachedView],
    );

    const currentQueryParams = {
        page: page - 1,
        workspaceId: workspaceId ?? "",
        limit: FINANCIAL_DOCUMENTS_PAGE_SIZE,
        sort: getSortExpression(sort),
    };

    const { uploadingDocuments, clearUploads } = useFinancialDocumentUpload();

    const { data: financialDocumentsResponse } = useFinancialDocumentsQuery({
        queryParams: {
            ...currentQueryParams,
            filters,
        },
        shouldRefetch: (data) => {
            const hasDocumentsWithoutMatchAttempt = data?.data?.some(
                (financialDocument) =>
                    financialDocument.transactionMatches &&
                    !financialDocument.transactionMatches.length,
            );
            return Boolean(
                uploadingDocuments.length > 0 ||
                hasDocumentsWithoutMatchAttempt,
            );
        },
    });

    const handleSortChange = useCallback(
        (newSort: FinancialDocumentSortValue) => {
            setSort(newSort);
            setPage(1);
        },
        [],
    );

    const totalPages = financialDocumentsResponse?.pageCount ?? 1;
    const pagination = useMemo(
        () => ({ currentPage: page, totalPages, onPageChange: setPage }),
        [page, totalPages],
    );

    const totalIncludingUploading =
        (financialDocumentsResponse?.total ?? 0) + uploadingDocuments.length;

    const upload = useMemo(
        () => (
            <FinancialDocumentsUpload
                render={(open) => (
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        loading={uploadingDocuments.length > 0}
                        onClick={open}
                        data-testid="financial-documents-upload-button"
                        hotKey="alt+n"
                        hotKeyOptions={{
                            description: "Upload document",
                            preventDefault: true,
                        }}
                    >
                        Upload
                    </ButtonWithLoader>
                )}
            />
        ),
        [uploadingDocuments.length],
    );

    useEffect(() => {
        if (
            uploadingDocuments.length &&
            uploadingDocuments.every((el) => !el.isUploading)
        ) {
            queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.lists(),
            });
            clearUploads();
        }
    }, [clearUploads, uploadingDocuments]);

    const totalCount = financialDocumentsResponse?.total ?? 0;
    const currentPageDocuments = financialDocumentsResponse?.data ?? [];
    const documentDetailsNavigation = useDocumentDetailsNavigation({
        documents: currentPageDocuments,
        shownDocumentId,
        showDocumentDetails,
    });

    const emptyView = useCallback(() => {
        if (totalIncludingUploading === 0) {
            if (hasActiveFilters) {
                return (
                    <EmptyState
                        header="The filters don't match any of the documents."
                        action={resetFilters}
                    />
                );
            }
            return (
                <DocumentsEmptyState header="Upload or drag and drop your files">
                    {upload}
                </DocumentsEmptyState>
            );
        }
        return null;
    }, [totalIncludingUploading, hasActiveFilters, resetFilters, upload]);

    return (
        <>
            <FinancialDocumentBulkSelectionContextProvider
                currentFilters={filters}
                totalCount={totalCount}
            >
                <FinancialDocumentBulkActionsProvider>
                    <article className={interfaceStyles.fullHeightContent}>
                        <TablePageHeader
                            search={filters.search}
                            onSearchChange={(value) => {
                                handleFiltersChange({ search: value ?? "" });
                            }}
                            filters={filters}
                            onFiltersChange={handleFiltersChange}
                            availableFilters={availableFilters}
                            shouldShowNoAccountOption
                            isFullPage
                        >
                            <EntityFilterCombobox
                                filters={filters}
                                onChange={handleFiltersChange}
                                showNoEntityOption
                            />
                            <FinancialDocumentsDownload />
                            <ColumnSettings
                                view={documentView}
                                onViewChange={handleViewChange}
                                viewConfig={financialDocumentsViewConfig}
                                defaultView={defaultFinancialDocumentView}
                            />
                            {upload}
                        </TablePageHeader>

                        <FinancialDocumentUploadDropzone
                            cover={
                                <>
                                    <span>
                                        <UploadIcon />
                                    </span>
                                    <p>Drop your files to upload</p>
                                </>
                            }
                        >
                            <FinancialDocumentsViewContainer
                                view={documentView}
                                documents={
                                    financialDocumentsResponse?.data ?? null
                                }
                                total={totalCount}
                                pagination={pagination}
                                onDocumentClick={(document) =>
                                    document && showDocumentDetails(document.id)
                                }
                                sort={sort}
                                onSortChange={handleSortChange}
                                emptyView={emptyView}
                            />
                        </FinancialDocumentUploadDropzone>
                    </article>
                </FinancialDocumentBulkActionsProvider>
            </FinancialDocumentBulkSelectionContextProvider>

            <StandardModal
                className="fullscreen-modal"
                show={!!shownDocumentId}
                onHide={closeDetails}
                fullscreen={isMaximized}
            >
                {shownDocumentId && (
                    <FinancialDocumentDetailsContainer
                        financialDocumentId={shownDocumentId}
                        isMaximized={isMaximized}
                        toggleMaximized={toggleMaximized}
                        shouldShowNavigation={
                            documentDetailsNavigation.shouldShowNavigation
                        }
                        canGoToPrevious={
                            documentDetailsNavigation.canGoToPrevious
                        }
                        canGoToNext={documentDetailsNavigation.canGoToNext}
                        goToPrevious={documentDetailsNavigation.goToPrevious}
                        goToNext={documentDetailsNavigation.goToNext}
                    />
                )}
            </StandardModal>
        </>
    );
};
